import { useState, useEffect } from 'react'
import { Download, FileText, Image, FileSpreadsheet, Presentation, Archive, Music, Video, Eye, Loader2, Trash2 } from 'lucide-react'
import { supabase } from '../services/supabase'
import type { MaterialAula } from '../services/supabase'
import { useToast } from '../hooks/useToast'

interface MaterialListProps {
    aulaId: string
    className?: string
    showUploadButton?: boolean
    onUploadClick?: () => void
    showDeleteButton?: boolean // Controlar se mostra botão de excluir
    showAddButton?: boolean // Controlar se mostra botão de adicionar
}

// Mapeamento de tipos de arquivo para ícones
const getFileIcon = (tipo: string) => {
    switch (tipo.toLowerCase()) {
        case 'pdf':
            return <FileText className="w-6 h-6 text-red-500" />
        case 'doc':
        case 'docx':
            return <FileText className="w-6 h-6 text-blue-500" />
        case 'xlsx':
        case 'xls':
            return <FileSpreadsheet className="w-6 h-6 text-green-500" />
        case 'pptx':
        case 'ppt':
            return <Presentation className="w-6 h-6 text-orange-500" />
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return <Image className="w-6 h-6 text-purple-500" />
        case 'mp4':
        case 'avi':
        case 'mov':
            return <Video className="w-6 h-6 text-indigo-500" />
        case 'mp3':
        case 'wav':
        case 'ogg':
            return <Music className="w-6 h-6 text-pink-500" />
        case 'zip':
        case 'rar':
        case '7z':
            return <Archive className="w-6 h-6 text-gray-500" />
        default:
            return <FileText className="w-6 h-6 text-gray-500" />
    }
}

// Função para formatar tamanho de arquivo
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Função para formatar data
const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

export default function MaterialList({
    aulaId,
    className = '',
    showUploadButton = false,
    onUploadClick,
    showDeleteButton = true, // Por padrão mostra
    showAddButton = true // Por padrão mostra
}: MaterialListProps) {
    const [materiais, setMateriais] = useState<MaterialAula[]>([])
    const [loading, setLoading] = useState(true)
    const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set())
    const { showSuccess, showError, showWarning } = useToast()

    // Carregar materiais da aula
    const loadMateriais = async () => {
        try {
            setLoading(true)
            console.log('🔍 MaterialList: Carregando materiais para aula:', aulaId)

            // LOG: Verificar usuário antes da query
            const { data: { user } } = await supabase.auth.getUser()
            console.log('👤 Usuário antes da query:', user?.email || 'Não logado')
            console.log('🔑 User ID:', user?.id || 'N/A')

            // LOG: Tentar a query
            console.log('🔍 Tentando SELECT na tabela material_aula...')

            const { data, error } = await supabase
                .from('material_aula')
                .select('*')
                .eq('aula_id', aulaId)
                .eq('status', 'ativo')
                .order('ordem_exibicao', { ascending: true })

            if (error) {
                console.error('❌ Erro na query:', error)
                console.error('❌ Código do erro:', error.code)
                console.error('❌ Mensagem:', error.message)
                console.error('❌ Detalhes:', error.details)
                console.error('❌ Hint:', error.hint)
                throw error
            }

            console.log('✅ Materiais encontrados:', data?.length || 0, data)
            setMateriais(data || [])
        } catch (error) {
            console.error('❌ Erro ao carregar materiais:', error)
            showError('Erro ao carregar materiais da aula')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        // LOG: Verificar usuário logado antes de carregar
        supabase.auth.getUser().then(({ data: { user } }) => {
            console.log('📧 MaterialList - Usuário logado:', user?.email || 'Não logado')
            console.log('📝 MaterialList - Usuário ID:', user?.id || 'N/A')
        })

        loadMateriais()
    }, [aulaId])

    // Gerar URL de download segura
    const generateDownloadUrl = async (material: MaterialAula): Promise<string> => {
        try {
            // Tentar obter URL assinada
            const { data, error } = await supabase.storage
                .from(material.bucket_name)
                .createSignedUrl(material.nome_arquivo, 3600) // 1 hora

            if (error) {
                throw error
            }

            return data.signedUrl
        } catch (error) {
            console.error('Erro ao gerar URL de download:', error)
            // Fallback para URL pública se disponível
            return material.url_arquivo
        }
    }

    // Fazer download do arquivo
    const handleDownload = async (material: MaterialAula) => {
        try {
            setDownloadingFiles(prev => new Set(prev).add(material.id))

            const downloadUrl = await generateDownloadUrl(material)

            // Criar link temporário para download
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = material.nome_original
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            showSuccess(`Download de ${material.nome_original} iniciado`)
        } catch (error) {
            console.error('Erro ao fazer download:', error)
            showError(`Erro ao fazer download de ${material.nome_original}`)
        } finally {
            setDownloadingFiles(prev => {
                const newSet = new Set(prev)
                newSet.delete(material.id)
                return newSet
            })
        }
    }

    // Visualizar arquivo (para imagens e PDFs)
    const handleView = (material: MaterialAula) => {
        const canView = ['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(material.tipo_arquivo.toLowerCase())

        if (canView) {
            window.open(material.url_arquivo, '_blank')
        } else {
            showWarning('Visualização não disponível para este tipo de arquivo')
        }
    }

    // Excluir material (arquivo do storage + registro da tabela)
    const handleDelete = async (material: MaterialAula) => {
        if (!confirm(`Tem certeza que deseja excluir "${material.nome_original}"?`)) {
            return
        }

        try {
            console.log('🗑️ Excluindo material:', material.nome_original)

            // 1. Deletar arquivo do storage
            const { error: storageError } = await supabase.storage
                .from(material.bucket_name)
                .remove([material.nome_arquivo])

            if (storageError) {
                console.error('❌ Erro ao deletar do storage:', storageError)
                // Continue mesmo se falhar no storage
            } else {
                console.log('✅ Arquivo removido do storage')
            }

            // 2. Deletar registro da tabela
            const { error: dbError } = await supabase
                .from('material_aula')
                .delete()
                .eq('id', material.id)

            if (dbError) {
                console.error('❌ Erro ao deletar do banco:', dbError)
                throw dbError
            }

            console.log('✅ Registro removido da tabela')
            showSuccess(`Material "${material.nome_original}" excluído com sucesso!`)

            // 3. Recarregar lista
            loadMateriais()
        } catch (error) {
            console.error('❌ Erro ao excluir material:', error)
            showError('Erro ao excluir material')
        }
    }

    if (loading) {
        return (
            <div className={`space-y-4 ${className}`}>
                <div className="flex items-center space-x-2">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                    <span className="text-gray-600">Carregando materiais...</span>
                </div>
            </div>
        )
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Cabeçalho */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                    Materiais da Aula ({materiais.length})
                </h3>
                {showUploadButton && showAddButton && onUploadClick && (
                    <button
                        type="button"
                        onClick={onUploadClick}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                        Adicionar Material
                    </button>
                )}
            </div>

            {/* Lista de materiais */}
            {materiais.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhum material disponível para esta aula</p>
                    {showUploadButton && showAddButton && onUploadClick && (
                        <button
                            type="button"
                            onClick={onUploadClick}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        >
                            Adicionar Primeiro Material
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {materiais.map((material) => (
                        <div
                            key={material.id}
                            className="flex items-center space-x-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                        >
                            {/* Ícone do arquivo */}
                            <div className="flex-shrink-0">
                                {getFileIcon(material.tipo_arquivo)}
                            </div>

                            {/* Informações do arquivo */}
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                    {material.nome_original}
                                </h4>
                                {material.descricao && (
                                    <p className="text-sm text-gray-600 truncate">
                                        {material.descricao}
                                    </p>
                                )}
                                <div className="flex items-center space-x-4 mt-1">
                                    <span className="text-xs text-gray-500">
                                        {formatFileSize(material.tamanho_arquivo)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {formatDate(material.created_at)}
                                    </span>
                                    <span className="text-xs text-gray-500 uppercase">
                                        {material.tipo_arquivo}
                                    </span>
                                </div>
                            </div>

                            {/* Botões de ação */}
                            <div className="flex items-center space-x-2">
                                {/* Botão de visualizar (para arquivos suportados) */}
                                {['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(material.tipo_arquivo.toLowerCase()) && (
                                    <button
                                        type="button"
                                        onClick={() => handleView(material)}
                                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                        title="Visualizar"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>
                                )}

                                {/* Botão de download */}
                                <button
                                    type="button"
                                    onClick={() => handleDownload(material)}
                                    disabled={downloadingFiles.has(material.id)}
                                    className="p-2 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                                    title="Download"
                                >
                                    {downloadingFiles.has(material.id) ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4" />
                                    )}
                                </button>

                                {/* Botão de excluir - apenas se showDeleteButton for true */}
                                {showDeleteButton && (
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(material)}
                                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                        title="Excluir"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Informações adicionais */}
            {materiais.length > 0 && (
                <div className="text-xs text-gray-500 text-center">
                    <p>
                        💡 Clique no ícone de download para baixar o arquivo ou no ícone de olho para visualizar
                    </p>
                </div>
            )}
        </div>
    )
}
